// SuperAdminDashboard.js
import React, { useState, useEffect } from "react";
import {
  Users,
  FileText,
  DollarSign,
  Search,
  X,
  Eye,
  EyeOff,
  updateProfile, Briefcase, PlusCircle, Trash2,
} from "lucide-react";
import { ref, onValue, update } from "firebase/database";
import { db,auth } from "./firebase"; // adjust path if needed
import { push, remove } from "firebase/database";

import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut
} from "firebase/auth";

const SuperAdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const ROOT_EMAIL = "info.scopewit@gmail.com"; 
    const [loading, setLoading] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalPapers: 0,
    freeUsers: 0,
    basePlanUsers: 0,
    premiumUsers: 0,
    revenue: 0,
  });

  const [jobs, setJobs] = useState([]);

  const [showJobForm, setShowJobForm] = useState(false);
const [newJob, setNewJob] = useState({
  company: "",
  title: "",
  requirements: "",
  details: "",
  campusType: "Open Campus",
  notes: "",
  academics: "",
  address: "",
  applyLink: "",
});



useEffect(() => {
  const jobsRef = ref(db, "jobs");
  onValue(jobsRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      const jobList = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
      }));
      setJobs(jobList);
    } else {
      setJobs([]);
    }
  });
}, []);


  const [users, setUsers] = useState([]);

  // ✅ Fetch users & stats from Firebase
  useEffect(() => {
    const usersRef = ref(db, "users");
    const statsRef = ref(db, "dashboardStats");

    // Listen for users
    onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const userList = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setUsers(userList);
      }
    });

    // Listen for stats
    onValue(statsRef, (snapshot) => {
      if (snapshot.exists()) {
        setDashboardStats(snapshot.val());
      }
    });
  }, []);

  // ✅ Admin login (demo)
// ✅ Send OTP link
  const handleLogin = () => {
    setLoading(true);
    const actionCodeSettings = {
      url: window.location.href, // same page
      handleCodeInApp: true,
    };

    sendSignInLinkToEmail(auth, ROOT_EMAIL, actionCodeSettings)
      .then(() => {
        window.localStorage.setItem("emailForSignIn", ROOT_EMAIL);
        alert("Login link sent to " + ROOT_EMAIL);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };
    const handleLogout = async () => {
    await signOut(auth);
    setIsLoggedIn(false);
  };


  // ✅ Update user status in Firebase
  const handleStatusToggle = (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    update(ref(db, `users/${userId}`), { status: newStatus });
  };

  // ✅ Update user plan in Firebase
  const handlePlanChange = (userId, newPlan) => {
    update(ref(db, `users/${userId}`), { plan: newPlan });
    setShowEditModal(false);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = selectedPlan === "all" || user.plan === selectedPlan;
    return matchesSearch && matchesPlan;
  });

   // ✅ Fetch jobs from Firebase
  useEffect(() => {
    const jobsRef = ref(db, "jobs");
    onValue(jobsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const jobList = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setJobs(jobList);
      } else {
        setJobs([]);
      }
    });
  }, []);

  // ✅ Check if link has OTP token
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem("emailForSignIn");
      if (!email) email = window.prompt("Please provide your email");
      signInWithEmailLink(auth, email, window.location.href)
        .then((result) => {
          if (result.user.email === ROOT_EMAIL) {
            setIsLoggedIn(true);
          } else {
            alert("Not authorized!");
          }
          window.localStorage.removeItem("emailForSignIn");
        })
        .catch((err) => console.error(err));
    }
  }, []);

  // ✅ Login screen
 if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Super Admin Login</h1>
          <p className="text-gray-600 mb-6">
            Only <strong>admin@karolinaenterprises.in</strong> can log in.
          </p>
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            {loading ? "Sending OTP..." : "Send OTP to Email"}
          </button>
        </div>
      </div>
    );
  }


  // ✅ Dashboard after login
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-10 h-10 rounded-lg flex items-center justify-center">
              <Users className="text-white" size={24} />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">
              Super Admin Dashboard
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Total Users</p>
            <p className="text-3xl font-bold text-gray-800">
              {dashboardStats.totalUsers}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Research Papers</p>
            <p className="text-3xl font-bold text-gray-800">
              {dashboardStats.totalPapers}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Revenue</p>
            <p className="text-3xl font-bold text-gray-800">
              ${dashboardStats.revenue}
            </p>
          </div>
        </div>

        {/* User Management */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              User Management
            </h2>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none"
                />
              </div>

              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none"
              >
                <option value="all">All Plans</option>
                <option value="free">Free</option>
                <option value="basic">Base</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>

          {/* User Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Papers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.plan === "premium"
                            ? "bg-purple-100 text-purple-800"
                            : user.plan === "basic"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.plan?.charAt(0).toUpperCase() +
                          user.plan?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.papers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.joinDate
                        ? new Date(user.joinDate).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() =>
                          handleStatusToggle(user.id, user.status)
                        }
                        className={
                          user.status === "active"
                            ? "text-red-600 hover:text-red-900"
                            : "text-green-600 hover:text-green-900"
                        }
                      >
                        {user.status === "active" ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Edit Plan Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                Edit User Plan
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-1">User</p>
              <p className="text-lg font-semibold text-gray-800">
                {selectedUser.name}
              </p>
              <p className="text-sm text-gray-500">{selectedUser.email}</p>
            </div>

            <div className="space-y-2">
              {["free", "basic", "premium"].map((plan) => (
                <button
                  key={plan}
                  onClick={() => handlePlanChange(selectedUser.id, plan)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition ${
                    selectedUser.plan === plan
                      ? "border-purple-600 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`font-semibold ${
                      plan === "premium"
                        ? "text-purple-600"
                        : plan === "basic"
                        ? "text-blue-600"
                        : "text-gray-600"
                    }`}
                  >
                    {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
{/* Job Management */}
<div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-8">
  <div className="p-6 border-b border-gray-200 flex justify-between items-center">
    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
      <Briefcase className="w-6 h-6 text-purple-600" /> Job Postings
    </h2>
    <button
      onClick={() => setShowJobForm(true)}
      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
    >
      <PlusCircle className="w-4 h-4" /> Add Job
    </button>
  </div>

  {/* Job Listings */}
  <div className="p-6">
    {jobs.length === 0 ? (
      <p className="text-gray-500">No job postings yet.</p>
    ) : (
      <div className="space-y-4">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="border border-gray-200 rounded-lg p-4 flex justify-between items-start"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {job.title} @ {job.company}
              </h3>
              <p className="text-sm text-gray-600">{job.requirements}</p>
              <p className="text-gray-700 mt-2">{job.details}</p>
              <p className="text-sm text-gray-500 mt-2">
                <strong>Campus:</strong> {job.campusType}
              </p>
              <p className="text-sm text-gray-500">
                <strong>Academics:</strong> {job.academics}
              </p>
              <p className="text-sm text-gray-500">
                <strong>Address:</strong> {job.address}
              </p>
              {job.notes && (
                <p className="text-sm text-gray-500 italic mt-1">{job.notes}</p>
              )}
              <a
                href={job.applyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline mt-2 inline-block"
              >
                Apply Here
              </a>
            </div>
            <button
              onClick={() => remove(ref(db, "jobs/" + job.id))}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
  {/* Job Form Modal */}
{/* Job Form Modal */}
{showJobForm && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 overflow-y-auto max-h-[90vh]">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-800">Add Job Posting</h3>
        <button
          onClick={() => setShowJobForm(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const jobsRef = ref(db, "jobs");
          push(jobsRef, {
            ...newJob,
            createdAt: Date.now(),
          });
          setNewJob({
            company: "",
            title: "",
            requirements: "",
            details: "",
            campusType: "Open Campus",
            workMode: "On-site",
            notes: "",
            academics: "",
            academicType: "Percentage",
            package: "",
            city: "",
            address: "",
            applyLink: "",
          });
          setShowJobForm(false);
        }}
        className="space-y-4"
      >
        {/* Company */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Company Name
          </label>
          <input
            type="text"
            value={newJob.company}
            onChange={(e) => setNewJob({ ...newJob, company: e.target.value })}
            required
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          />
        </div>

        {/* Job Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Job Title
          </label>
          <input
            type="text"
            value={newJob.title}
            onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
            required
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          />
        </div>

        {/* Requirements */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Requirements
          </label>
          <textarea
            value={newJob.requirements}
            onChange={(e) =>
              setNewJob({ ...newJob, requirements: e.target.value })
            }
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          ></textarea>
        </div>

        {/* Job Details */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Job Details
          </label>
          <textarea
            value={newJob.details}
            onChange={(e) => setNewJob({ ...newJob, details: e.target.value })}
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          ></textarea>
        </div>

        {/* Campus + Work Mode */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Campus Type
            </label>
            <select
              value={newJob.campusType}
              onChange={(e) =>
                setNewJob({ ...newJob, campusType: e.target.value })
              }
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            >
              <option>Open Campus</option>
              <option>Close Campus</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Work Mode
            </label>
            <select
              value={newJob.workMode}
              onChange={(e) =>
                setNewJob({ ...newJob, workMode: e.target.value })
              }
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            >
              <option>On-site</option>
              <option>Hybrid</option>
              <option>Remote</option>
            </select>
          </div>
        </div>

        {/* Package + City */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Annual Package (CTC)
            </label>
            <input
              type="text"
              placeholder="e.g. ₹5 LPA"
              value={newJob.package}
              onChange={(e) => setNewJob({ ...newJob, package: e.target.value })}
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Location (City)
            </label>
            <input
              type="text"
              placeholder="e.g. Pune, Maharashtra"
              value={newJob.city}
              onChange={(e) => setNewJob({ ...newJob, city: e.target.value })}
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            />
          </div>
        </div>

        {/* Academics Requirement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Academics Requirement
            </label>
            <input
              type="text"
              placeholder="e.g. 70 or 7.5"
              value={newJob.academics}
              onChange={(e) =>
                setNewJob({ ...newJob, academics: e.target.value })
              }
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Academic Type
            </label>
            <select
              value={newJob.academicType}
              onChange={(e) =>
                setNewJob({ ...newJob, academicType: e.target.value })
              }
              className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
            >
              <option value="Percentage">Percentage</option>
              <option value="CGPA">CGPA</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Full Address
          </label>
          <input
            type="text"
            value={newJob.address}
            onChange={(e) => setNewJob({ ...newJob, address: e.target.value })}
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Notes (Optional)
          </label>
          <textarea
            value={newJob.notes}
            onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          ></textarea>
        </div>

        {/* Apply Link */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Apply Link
          </label>
          <input
            type="url"
            value={newJob.applyLink}
            onChange={(e) =>
              setNewJob({ ...newJob, applyLink: e.target.value })
            }
            required
            className="mt-1 block w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
          />
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
        >
          Save Job Posting
        </button>
      </form>
    </div>
  </div>
)}

</div>


    </div>
  );
};

export default SuperAdminDashboard;
